import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { EMAIL, PHONE, PHONE_TEL, SERVICE_OPTIONS } from "@/data/site";
import { Eyebrow, Reveal } from "./Reveal";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const EMPTY = { name: "", phone: "", email: "", service: SERVICE_OPTIONS[0], address: "", message: "", website: "" };
const input = "w-full rounded-xl border border-[var(--leaf)]/15 bg-[var(--mist)] px-4 py-3.5 text-[15px] outline-none transition-[border-color,background-color] focus:border-[var(--leaf)] focus:bg-white";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[var(--muted)]">{label}</span>
    {children}
  </label>
);

const QuoteForm = () => {
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!API) { toast.error(`Please call us at ${PHONE}`); return; }
    setState("loading");
    try {
      await axios.post(`${API}/contact`, { ...f, email: f.email || null });
      setState("done");
      setF(EMPTY);
    } catch (err) {
      setState("idle");
      toast.error(err?.response?.status === 429 ? "Too many requests — please give us a call." : `Couldn't send. Please call ${PHONE}.`);
    }
  };

  if (state === "done") {
    return (
      <div data-testid="contact-success" className="flex h-full flex-col items-center justify-center py-16 text-center">
        <CheckCircle2 className="h-14 w-14 text-[var(--leaf)]" />
        <h3 className="mt-5 font-display text-3xl font-black">Request received!</h3>
        <p className="mt-3 max-w-xs text-[var(--muted)]">Hunter will reach out shortly. Need it sooner? Call {PHONE}.</p>
        <button data-testid="contact-send-another" onClick={() => setState("idle")} className="mt-6 font-bold text-[var(--leaf)] underline underline-offset-4">Send another</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="grid gap-4 sm:grid-cols-2">
      <Field label="Your name"><input required minLength={2} value={f.name} onChange={set("name")} className={input} data-testid="contact-name-input" /></Field>
      <Field label="Phone"><input required type="tel" minLength={7} value={f.phone} onChange={set("phone")} className={input} data-testid="contact-phone-input" /></Field>
      <Field label="Email (optional)"><input type="email" value={f.email} onChange={set("email")} className={input} data-testid="contact-email-input" /></Field>
      <Field label="Service">
        <select value={f.service} onChange={set("service")} className={input} data-testid="contact-service-select">
          {SERVICE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2"><Field label="Property address / town"><input value={f.address} onChange={set("address")} className={input} data-testid="contact-address-input" /></Field></div>
      <div className="sm:col-span-2"><Field label="Tell us about the project"><textarea rows={4} value={f.message} onChange={set("message")} className={`${input} resize-none`} data-testid="contact-message-input" /></Field></div>
      <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      <button type="submit" disabled={state === "loading"} data-testid="contact-submit-btn" className="flex items-center justify-center gap-2 rounded-full bg-[var(--leaf)] py-4 font-bold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[var(--leaf-deep)] disabled:opacity-70 sm:col-span-2">
        {state === "loading" && <Loader2 className="h-4 w-4 animate-spin" />} Request my free quote
      </button>
    </form>
  );
};

export const Contact = () => (
  <section id="contact" data-testid="contact-section" className="bg-[var(--mist)] py-24 sm:py-32">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1fr_1.15fr] lg:gap-20">
      <Reveal className="flex flex-col">
        <Eyebrow>Schedule a consultation</Eyebrow>
        <h2 className="mt-5 font-display text-5xl font-black leading-[0.92] sm:text-7xl">Let’s grow <span className="font-serif-i font-normal text-[var(--leaf)]">something.</span></h2>
        <p className="mt-6 max-w-md text-base text-[var(--muted)] md:text-lg">Send a request and we’ll get back to you — or just give us a call.</p>
        <a href={PHONE_TEL} data-testid="contact-call-link" className="group mt-10 flex items-center gap-4">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-[var(--leaf)] text-white transition-transform duration-300 group-hover:rotate-12"><Phone className="h-6 w-6" /></span>
          <span className="font-display text-3xl font-black sm:text-4xl group-hover:text-[var(--leaf)]">{PHONE}</span>
        </a>
        <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="mt-6 flex items-center gap-3 break-all font-semibold text-[var(--ink)] hover:text-[var(--leaf)]">
          <Mail className="h-5 w-5 shrink-0 text-[var(--leaf)]" /> {EMAIL}
        </a>
        <p className="mt-4 flex items-center gap-3 font-semibold"><MapPin className="h-5 w-5 text-[var(--leaf)]" /> Pleasant View, TN</p>
      </Reveal>
      <Reveal delay={0.1} className="rounded-[28px] bg-white p-6 shadow-[0_40px_80px_-50px_rgba(15,74,19,0.45)] sm:p-10">
        <QuoteForm />
      </Reveal>
    </div>
  </section>
);
